import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';
import { produce } from 'immer';

import type { SerializableNode, SerializerProps } from '@/serializer';
import { updateNodeText } from '@/utils/updateNodeText';

import { getElement } from '../../serializer/getElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import type { Author, SocialsData } from '../../types';

interface ExtendedSerializerProps extends SerializerProps {
  author: Author;
  node: JSONContent & SerializableNode;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    author,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
): ReactNode => {
  const key = createKey([node, path]);

  const isImage = node?.type === 'image';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';
  const isSocials = node?.type === 'socials';
  const mergeTag = node?.attrs?.mergeTag;
  const isRootContainerImage = node?.type === 'container' && rootStyle && node?.attrs?.backgroundImage;

  if (isRootContainerImage) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        backgroundImage: author?.profile_picture.url,
      },
    } as JSONContent & SerializableNode;
  }

  // Swap out images
  if (isImage && mergeTag) {
    if (mergeTag === 'author.image') {
      node = produce(node, (draft) => {
        if (draft.attrs) {
          draft.attrs.src = author?.profile_picture.url;
        }
      });
    }
  }

  // Swap out social links
  if (isSocials && mergeTag) {
    if (mergeTag === 'author.socials') {
      node = produce(node, (draft) => {
        if (draft.attrs) {
          draft.attrs.data = {
            discord_url: author?.discord_url,
            facebook_url: author?.facebook_url,
            instagram_url: author?.instagram_url,
            linkedin_url: author?.linkedin_url,
            tiktok_url: author?.tiktok_url,
            youtube_url: author?.youtube_url,
            twitter_url: author?.twitter_handle ? `https://www.x.com/${author?.twitter_handle}` : undefined,
          } as SocialsData;
        }
      });
    }
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && mergeTag) {
    if (mergeTag === 'author.name') {
      node = updateNodeText(node, author?.name) as JSONContent & SerializableNode;
    }
    if (mergeTag === 'author.bio') {
      node = updateNodeText(node, author?.bio) as JSONContent & SerializableNode;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          author,
        },
        [...path, i],
      ),
    ) || [];

  return getElement({
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
  });
};

export function AuthorSerializer(props: ExtendedSerializerProps) {
  return <>{serializeNode(props)}</>;
}
